import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SetterOrUpdater, useRecoilValue } from 'recoil';
import styled, { css } from 'styled-components';
import StyledButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import AccessAlarm from '@mui/icons-material/AccessAlarm';
import ArrowDropDownOutlined from '@mui/icons-material/ArrowDropDownOutlined';
import Lock from '@mui/icons-material/Lock';

import { Colors } from 'styles';
import { Button } from 'common/ui/Button';
import { Stack } from 'common/ui/Layout';
import Tooltip from 'common/ui/Tooltip';
import {
  ReportStatus,
  REPORT_EDITABLE_STATUSES,
  ReportStatuses,
  PROVISIONAL_SUBMIT_STATUSES,
} from 'domains/reporter/Reporter/state';
import getTimeRemaining from 'utils/getTimeRemaining';
import { useRecorder } from 'common/Recorder/useRecorder';
import { ReporterSaveState, reporterSavingState } from 'config/recoilState';
import { OnSubmitParams, SubmitType, VariantToStatusMapType } from '../types';
import { getButtonVariant } from './utils';
import { rootTheme } from 'theme/rootTheme';

const FINAL_COUNTDOWN_TIME_SECONDS = 60;

const ButtonGroup = styled(StyledButtonGroup)`
  .dropdown-button {
    padding: 0;
    width: 44px;
  }
`;

export type GetButtonContentParams = {
  isRecording: boolean;
  isBypass: boolean;
};

export type ButtonLabels = {
  buttonTooltip: string;
  buttonGroupAriaLabel: string;
  buttonDataTestId: string;
  splitButtonDataTestId: string;
};

type ActionButtonProps = {
  countdownSeconds: number | null;
  status: ReportStatuses;
  submitType?: SubmitType;
  onSubmit: (({ type }: OnSubmitParams) => void) | (({ type }: OnSubmitParams) => Promise<void>);
  onCancelSubmit: (() => void) | (() => Promise<void>);
  getContent: (params: GetButtonContentParams) => React.ReactElement | string;
  variantByStatus?: VariantToStatusMapType;
  disabled: boolean;
  options?: string[];
  labels: ButtonLabels;
  isBypassEnabled?: boolean;
  setBypass: SetterOrUpdater<boolean>;
  isCountdownMode?: boolean;
};

function ActionButton({
  countdownSeconds,
  status,
  submitType = SubmitType.Final,
  onSubmit,
  onCancelSubmit,
  getContent,
  variantByStatus,
  disabled,
  options = [],
  labels,
  isBypassEnabled = false,
  setBypass,
  isCountdownMode,
}: ActionButtonProps): React.ReactElement {
  const [splitButtonOpen, setSplitButtonOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0); // 0 is Action, 1 is Bypass and Action
  const time = getTimeRemaining(countdownSeconds);
  const isInFinalCountdownState =
    countdownSeconds != null && countdownSeconds <= FINAL_COUNTDOWN_TIME_SECONDS;
  const { isRecording } = useRecorder();
  const reportSaveState = useRecoilValue(reporterSavingState);
  const variant = getButtonVariant({
    status,
    variantByStatus,
    isInFinalCountdownState,
    submitType,
  });

  const isReportSaving = useMemo(() => {
    return reportSaveState === ReporterSaveState.Saving;
  }, [reportSaveState]);

  // Will disable the button if receives disabled prop, in-flight statuses matches the current status,
  // the nvoq queue of pending stable text is not empty or if actively recording
  const isRecordingProcessing =
    REPORT_EDITABLE_STATUSES.includes(status as (typeof REPORT_EDITABLE_STATUSES)[number]) &&
    isRecording;
  const isButtonDisabled = disabled || isRecordingProcessing || isReportSaving;

  const content = getContent({
    isRecording,
    isBypass: selectedIndex === 1,
  });

  const handleMenuItemClick = (event: React.MouseEvent, index: number) => {
    setSelectedIndex(index);
    setSplitButtonOpen(false);
  };

  const handleToggle = () => {
    setSplitButtonOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (event.target instanceof Node && anchorRef.current?.contains(event.target)) {
      return;
    }
    setSplitButtonOpen(false);
  };

  const handleClick = () => {
    if (
      PROVISIONAL_SUBMIT_STATUSES.includes(status as (typeof PROVISIONAL_SUBMIT_STATUSES)[number])
    ) {
      onCancelSubmit();
    } else {
      onSubmit({ type: submitType });
    }
  };

  useEffect(() => {
    setBypass(isBypassEnabled && selectedIndex === 1);
  }, [isBypassEnabled, selectedIndex, setBypass]);

  const showBypassButton = useMemo(() => {
    return (
      isBypassEnabled &&
      REPORT_EDITABLE_STATUSES.includes(status as (typeof REPORT_EDITABLE_STATUSES)[number])
    );
  }, [isBypassEnabled, status]);

  return (
    <Stack
      stretchX
      stretchY
      alignY="center"
      css={css`
        padding: 4px 0px;
        width: 100%;
      `}
    >
      {isCountdownMode && (
        <Stack
          alignY="center"
          stretchY
          css={`
            position: relative;
            background-color: ${isInFinalCountdownState ? Colors.gray10 : Colors.yellow6};
            color: ${Colors.gray1};
            display: flex;
            align-items: center;
            border-radius: ${rootTheme.radius.sm}px;
            right: -5rem;
            width: 12rem;
            padding-left: 1rem;
            height: 100%;
            padding: 0.36rem;
          `}
        >
          <AccessAlarm
            data-testid="countdown-icon"
            css={`
              color: ${isInFinalCountdownState ? Colors.red2 : 'inherit'};
              margin-right: 0.5rem;
            `}
          />
          {time}
        </Stack>
      )}
      <Tooltip content={isReportSaving ? labels.buttonTooltip : null}>
        <ButtonGroup
          variant="contained"
          ref={anchorRef}
          aria-label={labels.buttonGroupAriaLabel}
          sx={{
            '& .MuiButtonGroup-grouped': {
              borderRadius: rootTheme.radius.sm,
            },

            ...(isBypassEnabled
              ? {
                  '& .MuiButtonGroup-firstButton': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },

                  '& .MuiButtonGroup-lastButton': {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }
              : {}),

            '& .MuiButtonGroup-grouped:focus-visible': {
              zIndex: 1,
            },
          }}
        >
          <Button
            variant={variant}
            disabled={isButtonDisabled}
            icon={status === ReportStatus.Submitted ? <Lock /> : null}
            onClick={handleClick}
            data-status={status}
            data-testid={labels.buttonDataTestId}
            css={`
              white-space: nowrap;
              padding: 4px ${isCountdownMode ? '2.5rem' : '24px'};
              ${showBypassButton &&
              `border-radius: ${rootTheme.radius.sm}px 0px 0px ${rootTheme.radius.sm}px`};
            `}
          >
            {content}
          </Button>
          {showBypassButton && (
            <Button
              data-testid={labels.splitButtonDataTestId}
              className="dropdown-button"
              variant={variant}
              disabled={isButtonDisabled}
              aria-controls={splitButtonOpen ? 'split-button-menu' : undefined}
              aria-expanded={splitButtonOpen ? 'true' : undefined}
              aria-haspopup="menu"
              onClick={() => {
                !isButtonDisabled && handleToggle();
              }}
              css={`
                border-radius: 0px ${rootTheme.radius.sm}px ${rootTheme.radius.sm}px 0px;
                border-left: 1px solid ${Colors.gray10};
              `}
            >
              <Stack>
                <ArrowDropDownOutlined style={{ transform: 'rotate(180deg)' }} />
              </Stack>
            </Button>
          )}
        </ButtonGroup>
      </Tooltip>

      {options?.length > 0 && (
        <Popper open={splitButtonOpen} anchorEl={anchorRef.current} transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    {options.map((option, index) => (
                      <MenuItem
                        key={option}
                        selected={index === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, index)}
                        data-testid={`${labels.splitButtonDataTestId}-option-${options[index]}`}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Stack>
  );
}

export { ActionButton };
